import { TestType } from '@/models/SharedTypes';

/**
 * The only allowlist of exam banks the client may ask the backend for. Keyed by
 * the lowercase wire value. The retired `lcsw_free` is deliberately absent: old
 * emailed `/start?type=lcsw_free` links are still in the wild, and an unmatched
 * type must fall through to the user's real exam rather than mint a free
 * session (that default was the live paywall bypass).
 *
 * Built per call, NOT at module scope: the `utils` manual chunk is in a circular
 * cycle with `feature-dashboard`/`components`, so this module can evaluate before
 * the chunk defining `TestType`. A module-scope read of it crashed boot (#444).
 */
function allowedTestTypes(): Record<string, TestType> {
  return { lcsw: TestType.LCSW, lmsw: TestType.LMSW, lbsw: TestType.LBSW };
}

/** Case-insensitive lookup; anything unknown is `undefined`, never a cast. */
function matchTestType(value?: string | TestType | null): TestType | undefined {
  return typeof value === 'string' ? allowedTestTypes()[value.trim().toLowerCase()] : undefined;
}

/**
 * The single rule for which exam's questions a user gets. Values arrive from
 * untrusted places — a `?type=` URL param on a bookmarked/emailed link, a
 * Firestore `profile.targetExam`, a backend daily card — so every one is
 * matched against the allowlist above instead of cast. Unknown input never
 * throws and never reaches a bank lookup: it falls through to `fallback` (the
 * signed-in user's real target exam, where the caller has it) and then to null.
 * Null means "no exam is known": send the learner to pick one (/exam-date),
 * never guess a bank for them.
 *
 * Every quiz-start path must route through this. Several paths used to hardcode
 * `TestType.LCSW` / `LCSW_FREE`, which silently sent LMSW and LBSW users to LCSW
 * questions (a real bug: a trialing LMSW user practiced the wrong exam). The correct
 * logic already lived in ModernTestSelector; this extracts it so there is no "older
 * path" left to fall out of sync.
 */
export function resolveTestType(
  targetExam?: string | TestType | null,
  fallback?: string | TestType | null,
): TestType | null {
  return matchTestType(targetExam) ?? matchTestType(fallback) ?? null;
}
